import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, X, Pencil, Printer, ClipboardList, PackageMinus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { FormModal, Field, SectionTitle, StockTypeTabs } from "@/pages/InwardStock";
import { printOrder } from "@/pages/PurchaseOrders";

const SO_STATUSES = ["Draft", "Confirmed", "Packed", "Partially Dispatched", "Closed", "Cancelled"];
const CATEGORIES = ["E-Rickshaw", "Solar", "Automotive", "Inverter", "Custom"];

const statusClass = (s) => {
  if (s === "Closed") return "badge-ok";
  if (s === "Cancelled") return "badge-danger";
  if (s === "Draft") return "bg-zinc-100 text-zinc-600 border border-zinc-300 rounded-full px-2 py-0.5 text-[11px] font-bold";
  return "bg-[#E3F0FF] text-[#145B98] border border-[#B8D6F0] rounded-full px-2 py-0.5 text-[11px] font-bold";
};

export default function SalesOrders() {
  const { user } = useAuth();
  const [stockType, setStockType] = useState("ready");
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [show, setShow] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty("ready"));

  function empty(st) {
    return {
      stock_type: st || "ready",
      customer_id: "", date: new Date().toISOString().slice(0, 10), delivery_date: "",
      status: "Draft", category: "E-Rickshaw", item: "", model: "", quantity: 1, rate: 0, tax_percent: 0, notes: "",
    };
  }

  const load = async () => {
    const [s, c] = await Promise.all([api.get(`/sales-orders?stock_type=${stockType}`), api.get("/customers")]);
    setItems(s.data); setCustomers(c.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [stockType]);

  const close = () => { setShow(false); setEditingId(null); setForm(empty(stockType)); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        stock_type: form.stock_type || stockType,
        quantity: parseInt(form.quantity),
        rate: parseFloat(form.rate),
        tax_percent: parseFloat(form.tax_percent || 0),
      };
      if (editingId) { await api.patch(`/sales-orders/${editingId}`, payload); toast.success("SO updated"); }
      else { await api.post("/sales-orders", payload); toast.success("SO created"); }
      close(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const startEdit = (x) => {
    setEditingId(x.id);
    setForm({
      stock_type: x.stock_type || "ready",
      customer_id: x.customer_id, date: x.date, delivery_date: x.delivery_date || "", status: x.status,
      category: x.category || "E-Rickshaw", item: x.item || "", model: x.model || "",
      quantity: x.quantity, rate: x.rate, tax_percent: x.tax_percent, notes: x.notes || "",
    });
    setShow(true);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this Sales Order?")) return;
    try { await api.delete(`/sales-orders/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const convert = async (x) => {
    if (x.status === "Closed") { toast.error("SO already converted"); return; }
    const batch = window.prompt(`Convert SO ${x.so_number} to Outward Dispatch.\n\nEnter the batch number to dispatch from (must be in stock):`, "");
    if (!batch) return;
    try {
      await api.post(`/sales-orders/${x.id}/convert-to-outward`, { batch_number: batch });
      toast.success(`Converted to Outward. Batch: ${batch}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Convert failed"); }
  };

  const print = (x) => printOrder({
    title: "SALES ORDER", number: x.so_number, date: x.date, party: x.customer_name,
    partyLabel: "Customer", extraDateLabel: "Delivery Date", extraDate: x.delivery_date,
    item: x.item, model: x.model, category: x.category, quantity: x.quantity, rate: x.rate,
    tax_percent: x.tax_percent, total: x.total, notes: x.notes, status: x.status,
  });

  const total = (parseFloat(form.rate || 0) * parseInt(form.quantity || 0)) * (1 + parseFloat(form.tax_percent || 0) / 100);

  return (
    <div className="p-8 space-y-6" data-testid="so-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="overline mb-2"><span className="inline-block w-6 h-[2px] bg-[#28B516] mr-2 align-middle" />Sales · {stockType === "raw" ? "Raw Material" : "Ready Stock"}</div>
          <h1 className="font-display text-4xl uppercase text-zinc-900">Sales Orders</h1>
        </div>
        <button onClick={() => { setForm(empty(stockType)); setShow(true); }} className="btn-primary flex items-center gap-2" data-testid="new-so-btn"><Plus className="w-4 h-4" /> New Sales Order</button>
      </div>

      <StockTypeTabs value={stockType} onChange={setStockType} testidPrefix="so" />

      <div className="border border-zinc-200 bg-white rounded-2xl overflow-x-auto">
        <table className="data-table" data-testid="so-table">
          <thead><tr><th>SO No.</th><th>Date</th><th>Customer</th><th>Item</th><th className="text-right">Qty</th><th className="text-right">Total ₹</th><th>Delivery</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan="9" className="text-center py-12 text-zinc-500"><ClipboardList className="w-10 h-10 mx-auto mb-3 opacity-40" />No sales orders yet.</td></tr>}
            {items.map((x) => (
              <tr key={x.id}>
                <td className="font-mono font-bold text-[#28B516]">{x.so_number}</td>
                <td>{x.date}</td>
                <td className="font-semibold">{x.customer_name}</td>
                <td>{x.item}<div className="text-xs text-zinc-500">{x.model}</div></td>
                <td className="text-right font-mono">{x.quantity}</td>
                <td className="text-right font-mono font-bold text-[#28B516]">{x.total.toLocaleString()}</td>
                <td>{x.delivery_date || "—"}</td>
                <td><span className={statusClass(x.status)}>{x.status}</span></td>
                <td>
                  <div className="flex gap-2">
                    <button onClick={() => print(x)} className="text-zinc-400 hover:text-[#1769AA] transition-colors" title="Print" data-testid={`print-so-${x.id}`}><Printer className="w-4 h-4" /></button>
                    {x.status !== "Closed" && x.status !== "Cancelled" && (user?.role === "admin" || x.created_by === user?.email) && (
                      <button onClick={() => convert(x)} className="text-zinc-400 hover:text-[#28B516] transition-colors" title="Convert to Outward Dispatch" data-testid={`convert-so-${x.id}`}><PackageMinus className="w-4 h-4" /></button>
                    )}
                    {(user?.role === "admin" || x.created_by === user?.email) && (
                      <>
                        <button onClick={() => startEdit(x)} className="text-zinc-400 hover:text-[#28B516] transition-colors" title="Edit" data-testid={`edit-so-${x.id}`}><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => remove(x.id)} className="text-zinc-400 hover:text-[#D9363E] transition-colors" title="Delete" data-testid={`del-so-${x.id}`}><Trash2 className="w-4 h-4" /></button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {show && (
        <FormModal onClose={close}>
          <form onSubmit={submit} className="space-y-5" data-testid="so-form">
            <div className="flex items-center justify-between border-b border-zinc-200 pb-4">
              <h2 className="font-display text-2xl uppercase text-zinc-900">{editingId ? "Edit Sales Order" : "New Sales Order"}</h2>
              <button type="button" onClick={close} className="text-zinc-500 hover:text-zinc-900"><X className="w-5 h-5" /></button>
            </div>
            <SectionTitle>Order Details</SectionTitle>
            <div className="flex gap-2 mb-2" data-testid="so-stock-type">
              {[{ k: "ready", label: "Ready Stock" }, { k: "raw", label: "Raw Material" }].map((o) => (
                <button
                  type="button"
                  key={o.k}
                  onClick={() => setForm({ ...form, stock_type: o.k })}
                  data-testid={`so-stock-type-${o.k}`}
                  className={`px-4 py-2 border text-xs uppercase font-bold tracking-widest transition-colors ${
                    form.stock_type === o.k ? "bg-[#28B516] border-[#28B516] text-white" : "bg-white border-zinc-200 text-zinc-600 hover:border-[#28B516] hover:text-[#28B516]"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Field label="SO Date*"><input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="input-field" data-testid="so-date" /></Field>
              <Field label="Customer*">
                <select required value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} className="input-field" data-testid="so-customer">
                  <option value="">Select customer…</option>
                  {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </Field>
              <Field label="Status">
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="input-field" data-testid="so-status">
                  {SO_STATUSES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Delivery Date"><input type="date" value={form.delivery_date} onChange={(e) => setForm({ ...form, delivery_date: e.target.value })} className="input-field" data-testid="so-delivery" /></Field>
              <Field label="Category">
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input-field" data-testid="so-category">
                  {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Item*"><input required value={form.item} onChange={(e) => setForm({ ...form, item: e.target.value })} className="input-field" data-testid="so-item" /></Field>
              <Field label="Model"><input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="input-field" data-testid="so-model" /></Field>
              <Field label="Quantity*"><input required type="number" min="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="input-field font-mono" data-testid="so-qty" /></Field>
              <Field label="Rate ₹*"><input required type="number" step="0.01" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} className="input-field font-mono" data-testid="so-rate" /></Field>
              <Field label="Tax %"><input type="number" step="0.01" value={form.tax_percent} onChange={(e) => setForm({ ...form, tax_percent: e.target.value })} className="input-field font-mono" data-testid="so-tax" /></Field>
              <Field label="Total (auto)"><div className="input-field font-mono text-[#28B516] font-bold" data-testid="so-total">₹{total.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div></Field>
              <Field label="Notes / Terms" full><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input-field h-20 resize-none" data-testid="so-notes" /></Field>
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-zinc-200">
              <button type="button" onClick={close} className="btn-ghost">Cancel</button>
              <button type="submit" className="btn-primary" data-testid="submit-so">{editingId ? "Update SO" : "Create SO"}</button>
            </div>
          </form>
        </FormModal>
      )}
    </div>
  );
}
